// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as R from 'ramda'
import { createAction } from 'redux-actions'
import { CallType } from '@weblite/ts-action/media-proxy'
import { CallMessageStatus } from '@weblite/ts-action/message'
import { CallState } from './types'
import { dispatch, getState } from '$setup/redux'

export interface CallStoragePvMeta {
  roomId: string
  state: CallState
  status?: CallMessageStatus
  isCaller?: boolean
  duration?: number
  acceptingIncomingCall?: boolean
}

export interface CallStorageHallMeta {
  name: string
  isOwner?: boolean
  isParticipating?: boolean
}

export interface CallStorage {
  roomId: string
  callType?: CallType

  muted: boolean
  hasNextCall?: boolean
  startedAt?: number
  joinedAt?: number

  pvMeta: CallStoragePvMeta
  hallMeta: CallStorageHallMeta
  recordMode: boolean
}

export interface CallStorageUpdate
  extends Omit<CallStorage, 'pvMeta' | 'hallMeta'> {
  pvMeta: Partial<CallStoragePvMeta>
  hallMeta: Partial<CallStorageHallMeta>
}

const initialState: CallStorage = {
  roomId: '',
  callType: undefined,

  muted: true,
  hasNextCall: false,
  startedAt: undefined,
  joinedAt: undefined,

  pvMeta: {
    roomId: '',
    state: 'none',
  },
  hallMeta: {
    name: '',
  },
  recordMode: false,
}

// actions
const RESET_CALL = 'RESET_CALL'
const resetCall = createAction(RESET_CALL)

const UPDATE_CALL = 'UPDATE_CALL'
const updateCall = createAction(UPDATE_CALL)

const RESET_CALL_PV_META = 'RESET_CALL_PV_META'
const resetCallPvMeta = createAction(RESET_CALL_PV_META)

const UPDATE_CALL_PV_META = 'UPDATE_CALL_PV_META'
const updateCallPvMeta = createAction(UPDATE_CALL_PV_META)

const RESET_CALL_HALL_META = 'RESET_CALL_HALL_META'
const resetCallHallMeta = createAction(RESET_CALL_HALL_META)

const UPDATE_CALL_HALL_META = 'UPDATE_CALL_HALL_META'
const updateCallHallMeta = createAction(UPDATE_CALL_HALL_META)

const SET_CALL_RECORD_MODE = 'SET_CALL_RECORD_MODE'
const setCallRecordMode = createAction(SET_CALL_RECORD_MODE)

export const callStorage = {
  get: () => getState().main.call as CallStorage,
  set: (call: Partial<CallStorageUpdate>) => dispatch(updateCall(call)),
  reset: () => dispatch(resetCall()),

  getPvMeta: () => (getState().main.call as CallStorage).pvMeta,
  setPvMeta: (data: Partial<CallStoragePvMeta>) =>
    dispatch(updateCallPvMeta(data)),
  resetPvMeta: () => dispatch(resetCallPvMeta()),

  getHallMeta: () => (getState().main.call as CallStorage).hallMeta,
  setHallMeta: (data: Partial<CallStorageHallMeta>) =>
    dispatch(updateCallHallMeta(data)),
  resetHallMeta: () => dispatch(resetCallHallMeta()),

  setRecordMode: (recordMode: boolean) =>
    dispatch(setCallRecordMode(recordMode)),
}

// reducers
const reducers = {
  [RESET_CALL]: (state: CallStorage) => ({
    ...initialState,
    recordMode: state.recordMode,
  }),

  [UPDATE_CALL]: (state: CallStorage, update: Partial<CallStorage>) =>
    R.mergeDeepRight(state, update),

  [RESET_CALL_PV_META]: (state: CallStorage) => ({
    ...state,
    pvMeta: initialState.pvMeta,
  }),
  [UPDATE_CALL_PV_META]: (
    state: CallStorage,
    update: Partial<CallStoragePvMeta>,
  ) => ({
    ...state,
    pvMeta: { ...state.pvMeta, ...update },
  }),

  [RESET_CALL_HALL_META]: (state: CallStorage) => ({
    ...state,
    hallMeta: initialState.hallMeta,
  }),
  [UPDATE_CALL_HALL_META]: (
    state: CallStorage,
    update: Partial<CallStorageHallMeta>,
  ) => ({
    ...state,
    hallMeta: { ...state.hallMeta, ...update },
  }),
  [SET_CALL_RECORD_MODE]: (state: CallStorage, recordMode: boolean) => ({
    ...state,
    recordMode,
  }),
}

export const callReducer = (
  state = initialState,
  { type, payload }: { type: keyof typeof reducers; payload: any },
) => (reducers[type] ? reducers[type](state, payload) : state)
